import React, { useState, useEffect, useLayoutEffect, useRef, useCallback, useMemo } from 'react';
import useModalWindow from '../common/useModalWindow';
import { useTranslation } from 'react-i18next';
import { FiShare2, FiX, FiRefreshCw, FiPlay, FiPause, FiSquare, FiExternalLink, FiLogIn, FiMaximize2, FiMinimize2, FiSettings, FiBarChart2, FiLayers, FiUser, FiBookmark, FiActivity, FiInfo } from 'react-icons/fi';
import { toast } from 'react-toastify';
import PanelEditor from './PanelEditor';
import PanelSlotGrid from './PanelSlotGrid';
import SettingsTab from './SettingsTab';
import DetachablePanel from '../DetachablePanel';
import { useConfirm } from '../ConfirmDialog';
import { getRequiredVariables } from './bindUtils';
import styles from './MonitoringDashboardModal.module.css';

/**
 * 공용 HTTP 에러 문자열 빌더 — 서버 에러 메시지 + HTTP 상태코드.
 * 404 는 "백엔드 재시작 필요 가능성" 힌트 제공.
 */
async function describeHttpError(res, fallback, tr) {
  let serverMsg = '';
  try {
    const body = await res.json();
    serverMsg = body?.error || body?.message || '';
    // 코드가 있고 번역이 있으면 그것으로(서버 문구는 한국어다) — formatApiError 와 같은 규칙
    const coded = body?.errorType ? tr(`common:errors.${body.errorType}`, { defaultValue: '' }) : '';
    if (coded) return coded;
  } catch { /* not JSON */ }
  const suffix = ` (HTTP ${res.status})`;
  if (res.status === 404) {
    return (serverMsg || fallback) + tr('monitoring:dash.toast.mayNeedRestart', { suffix: suffix });
  }
  return (serverMsg || fallback) + suffix;
}

// localStorage 키 — 마지막으로 사용한 탭 id 보관 (모달 닫고 재오픈 시 복원).
// 합성 탭 (설정 등) 도 그대로 저장 — 사용자가 마지막에 보고있던 화면을 그대로 재현.
const STORAGE_KEY_LAST_TAB = 'livesql-monitoring-last-tab';

/**
 * 순환 전환 효과(2026-10-18, 발표 슬라이드처럼) — 키는 서버 panelStore.ROTATION_TRANSITIONS 와 같아야 한다('none' 은 효과 없음).
 * **나가는 화면과 들어오는 화면을 함께** 움직인다(in/out). 들어오는 화면은 순환하는 동안 **미리 그려 둔다**(보이지 않는 층) —
 * 그래야 전환 중에 패널이 비었다가 데이터가 늦게 차며 튀지 않는다(처음 판은 들어오는 화면에만 효과를 걸어, 빈 화면이 나타났다가 차는 것이 보였다 — 신고).
 * 순환 화면은 멀리서 보는 관제 화면이라 UI 의 짧은 전환(120~200ms)보다 길게, 양끝이 부드러운 곡선으로 둔다
 */
const ROTATION_EFFECT_MS = 900;
const ROTATION_EASING = 'cubic-bezier(0.45, 0, 0.25, 1)';
const ROTATION_EFFECTS = {
  fade: {   // 밝아지기 — 앞 화면이 배경으로 서서히 사라진 **뒤** 새 화면이 서서히 나타난다(발표 슬라이드의 「밝기 변화」).
    // 겹쳐 바꾸면(cross-fade) 빽빽한 차트 두 장의 선·막대가 가운데서 뒤엉켜 보였다(화면 확인 2026-10-18 — 다크 모드에서 특히)
    in: [{ opacity: 0, offset: 0 }, { opacity: 0, offset: 0.45 }, { opacity: 1, offset: 1 }],
    out: [{ opacity: 1, offset: 0 }, { opacity: 0, offset: 0.5 }, { opacity: 0, offset: 1 }],
  },
  slide: {  // 밀어내기 — 새 화면이 오른쪽에서 들어오며 앞 화면을 왼쪽으로 민다
    in: [{ transform: 'translate3d(100%, 0, 0)' }, { transform: 'translate3d(0, 0, 0)' }],
    out: [{ transform: 'translate3d(0, 0, 0)' }, { transform: 'translate3d(-100%, 0, 0)' }],
  },
  wipe: {   // 닦아내기 — 앞 화면 위로 새 화면이 왼쪽부터 드러난다
    in: [{ clipPath: 'inset(0 100% 0 0)' }, { clipPath: 'inset(0 0% 0 0)' }],
    out: null,
  },
  zoom: {   // 확대 — 새 화면이 살짝 큰 데서 제자리로 오며 나타나고, 앞 화면은 아주 조금 물러난다(흐리지 않는다 — 가운데서 어두워진다)
    in: [{ opacity: 0, transform: 'scale(1.04)' }, { opacity: 1, transform: 'scale(1)' }],
    out: [{ transform: 'scale(1)' }, { transform: 'scale(0.98)' }],
  },
  mosaic: { // 모자이크 — 새 화면이 바둑판 조각으로 무작위 순서로 하나씩 번져 나타난다(앞 화면은 그 아래 그대로). 키프레임이 아니라 가림막(mask)을 매 프레임 바꾼다
    in: null,
    out: null,
    mosaic: true,
  },
};
const MOSAIC_COLS = 16;
const MOSAIC_ROWS = 10;
// 조각 하나가 바뀌는 데 드는 몫(전체 시간 대비) — 짧게. 길면 조각 안에서 두 화면이 반투명하게 섞여 지저분했다(화면 확인 2026-10-18).
// 발표 슬라이드의 「디졸브」 처럼 조각마다 앞 화면이 사라지는 자리에 새 화면이 곧바로 나타난다(사라짐과 등장이 함께)
const MOSAIC_FADE = 0.06;
/** 조각마다 나타나기 시작하는 때(0~1-FADE) — 매번 같은 순서면 단조로워 전환마다 섞는다 */
function mosaicStarts(seed) {
  const n = MOSAIC_COLS * MOSAIC_ROWS;
  const order = Array.from({ length: n }, (_, i) => i);
  let h = (seed * 2654435761) >>> 0;
  for (let i = n - 1; i > 0; i--) {
    h = (Math.imul(h ^ (h >>> 15), 2246822519) + i) >>> 0;
    const j = h % (i + 1);
    [order[i], order[j]] = [order[j], order[i]];
  }
  const starts = new Array(n);
  order.forEach((tile, k) => { starts[tile] = (k / n) * (1 - MOSAIC_FADE); });
  return starts;
}
/** 진행(0~1)일 때의 가림막 — 조각마다 투명도가 다른 SVG. 다 드러난 조각은 1 */
export function mosaicMask(starts, progress) {
  let rects = '';
  for (let i = 0; i < starts.length; i++) {
    const a = Math.max(0, Math.min(1, (progress - starts[i]) / MOSAIC_FADE));
    if (a <= 0) continue;
    const x = i % MOSAIC_COLS; const y = Math.floor(i / MOSAIC_COLS);
    rects += `<rect x='${x}' y='${y}' width='1.02' height='1.02' fill='black' fill-opacity='${a.toFixed(3)}'/>`;
  }
  const svg = `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 ${MOSAIC_COLS} ${MOSAIC_ROWS}' preserveAspectRatio='none'>${rects}</svg>`;
  return `url("data:image/svg+xml,${encodeURIComponent(svg)}")`;
}

/** 순환 순서 — 저장된 순서(id 목록)대로, 거기 없는 화면은 뒤에 원래 탭 순서대로 */
export const byRotationOrder = (list, order) => {
  const pos = new Map((Array.isArray(order) ? order : []).map((id, i) => [id, i]));
  return list
    .map((x, i) => ({ x, k: pos.has(x.id) ? pos.get(x.id) : 1e6 + i }))
    .sort((a, b) => a.k - b.k)
    .map(o => o.x);
};

/**
 * 분리 popup 의 OS-fullscreen 감지기.
 *
 * popup 의 wrapper 안에 숨김 span 으로 마운트되어, ownerDocument.defaultView 로
 * popup window 객체를 얻고 resize 를 listen. innerHeight 가 screen.availHeight 의
 * 상당 부분을 차지하면 fullscreen 으로 간주.
 *
 * onChange ref 로 두어 effect 재실행 없이 최신 콜백 호출.
 */
function PopupFullscreenDetector({ onChange }) {
  const ref = useRef(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const popupWindow = el.ownerDocument.defaultView;
    // 메인 window 와 같으면 (portal 이 아니라 메인 트리에 마운트된 경우) 항상 false.
    if (!popupWindow || popupWindow === window) {
      onChangeRef.current(false);
      return;
    }
    const check = () => {
      const screenH = popupWindow.screen?.availHeight || popupWindow.screen?.height || 0;
      const screenW = popupWindow.screen?.availWidth || popupWindow.screen?.width || 0;
      // F11 fullscreen 또는 OS 창 최대화 시: innerHeight ≈ screen height.
      // 80px 여유는 macOS 메뉴바/Windows 작업표시줄/브라우저 chrome 잔여 등 변동치.
      const tallEnough = screenH > 0 && popupWindow.innerHeight >= screenH - 80;
      const wideEnough = screenW > 0 && popupWindow.innerWidth  >= screenW - 80;
      onChangeRef.current(tallEnough && wideEnough);
    };
    popupWindow.addEventListener('resize', check);
    check();
    return () => popupWindow.removeEventListener('resize', check);
  }, []);
  return <span ref={ref} style={{ display: 'none' }} aria-hidden="true" />;
}

// 합성 탭(프론트 로컬 전용, 백엔드 미저장). 탭 바 맨 앞에 노출.
// DataSource 관리는 별도 최상위 메뉴(모니터링 > 데이터소스)로 이전됐다.
const SETTINGS_TAB_ID = '__settings__';
const getSettingsTab = (t) => ({
  id: SETTINGS_TAB_ID,
  name: t('monitoring:dash.settings'),
  kind: 'settings',
  includeInRotation: false,
});
const SYNTHETIC_TAB_IDS = new Set([SETTINGS_TAB_ID]);

// 기본 탭 → 같은 내용을 더 자세히 보는 분석 창(Header 의 OWN_CONN_WINDOWS 키). 대시보드 값은 상시 수집 기록이고,
// 분석 창은 ASH·SYSMETRIC·AWR 로 그 순간을 더 정확히 본다(진단 팩 필요).
const RELATED_WINDOWS = {
  sessions: ['oracle-perf', 'session-admin', 'lock-wait'],
  wait: ['oracle-perf', 'lock-wait'],
  sysstat: ['oracle-perf'],
  memory: ['resource-analysis'],
};

const MonitoringDashboardModal = ({ isOpen, onClose, connections, onOpenWindow, onOpenCollectionSettings }) => {
  const [useDemo, setUseDemo] = useState(false);
  // 대상 DB — 내장 탭의 패널은 conn_id 조건이 없어 볼 수 있는 대상이 여럿이면 한 그래프에 섞였다(점검 2026-10-03).
  // 등록된 대상 중 하나를 고르고 서버가 그 대상의 기록만 담는다. 탭에 고정된 대상(사용자 기준·마법사 대시보드)이 있으면 그것이 이긴다
  const [targetInstanceId, setTargetInstanceId] = useState('');
  useEffect(() => { if (isOpen) setUseDemo(false); }, [isOpen]);
  const { t } = useTranslation();
  const confirm = useConfirm();
  // selectedConnId 는 상단 커넥션 셀렉터 제거 후에도 "기본값 후보" 용도로 유지.
  // Overview 탭이 별도 DS 선택기를 갖게 되므로 전체 대시보드 통제는 DS 로 이관된다.
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [refreshInterval, setRefreshInterval] = useState(30);
  const [timeWindowMin, setTimeWindowMin] = useState(60);
  // 일시 구간(2026-10-28 사용자 요청) — 정하면 최근 N분 대신 [시작, 끝] 을 본다(로컬 시각 글 'YYYY-MM-DDTHH:MM'). 적용한 것만 조회에 쓴다
  const [customRange, setCustomRange] = useState(null);
  const [rangeDraft, setRangeDraft] = useState(null);
  const rangeMin = customRange ? Math.max(1, Math.round((new Date(customRange.toAt) - new Date(customRange.fromAt)) / 60000)) : null;
  const rangeDomain = useMemo(() => (customRange ? [new Date(customRange.fromAt).getTime(), new Date(customRange.toAt).getTime()] : null), [customRange]);
  // 노드 목록(2026-10-16, RAC) — 고르기는 **패널 정의**에서 한다(2026-10-18). 여기서는 대상의 수집 기록에 있는 노드를 받아 편집 화면에 넘긴다(서버 /nodes)
  const [nodeList, setNodeList] = useState([]);
  const [datasources, setDatasources] = useState([]);
  const [queryTemplates, setQueryTemplates] = useState([]);

  const [tabs, setTabs] = useState([]);
  // 마지막 사용 탭 복원 — localStorage 에 저장된 값 우선, 없으면 'overview' 폴백.
  // tabs 로드 후 이 id 가 더 이상 유효하지 않으면 별도 effect 가 첫 비합성 탭으로 보정.
  const [activeTabId, setActiveTabId] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY_LAST_TAB);
      return saved || 'overview';
    } catch { return 'overview'; }
  });
  const [detachedTabIds, setDetachedTabIds] = useState(() => new Set());
  const [instances, setInstances] = useState([]);

  // 순환(rotation)
  const [rotation, setRotation] = useState({ enabled: false, dwellSec: 10, transition: 'fade' });
  // 순환할 때 화면이 바뀌는 효과(2026-10-18 — 「전환이 투박하다」). 내용을 다시 만들지 않고 그릇에 애니메이션만 건다(패널 상태·데이터가 그대로)
  const layerRefs = useRef(new Map());     // 탭 id → 층 요소
  const rotatedRef = useRef(false);
  const prevShownRef = useRef(null);         // 바뀌기 전 화면(나가는 화면)
  const [swap, setSwap] = useState(null);    // { from, to, n } — 지금 전환 중인 짝
  // 다음 화면을 미리 그리기 시작해도 되는 화면 — 전환이 **끝난 뒤**에 연다. 바뀌는 순간에 다음다음 화면의 차트까지 함께 그리면
  // 그 프레임이 260ms 쯤 멈춰 전환 첫머리가 덜컹거렸다(프레임 측정 2026-10-18)
  const [preloadFor, setPreloadFor] = useState(null);
  useEffect(() => {
    setPreloadFor(null);
    const id = activeTabId;
    const timer = setTimeout(() => setPreloadFor(id), ROTATION_EFFECT_MS + 300);
    return () => clearTimeout(timer);
  }, [activeTabId]);
  const rotationTimerRef = useRef(null);
  // 잠시 멈춤(2026-10-18) — 저장하지 않는 이 창만의 상태. 계속하면 지금 탭에서 이어 돈다
  const [rotationPaused, setRotationPaused] = useState(false);
  // 지금 **화면에 보이는** 탭 — 순환을 멈출 때 그 탭에 멈춘다(activeTabId 가 탭 줄에 없는 탭이면 화면은 첫 탭을 보인다)
  const shownTabIdRef = useRef(null);

  // 패널 편집기 (slotIndex: 신규 패널 생성 시 지정 슬롯 위치)
  const [editorState, setEditorState] = useState({ open: false, tabId: null, panel: null, slotIndex: null });

  // 전체화면 토글 (Electron/브라우저 창 꽉 채움, CSS 기반)
  const [isFullscreen, setIsFullscreen] = useState(false);

  // 분리 popup 의 OS-fullscreen(F11/창 최대화) 상태 — tabId → boolean.
  // PopupFullscreenDetector 가 popup window 의 resize 를 보고 갱신.
  // 활성화 시 popup wrapper 에 modalFullscreen className 추가 + isFullscreen=true 로
  // 폰트/패딩 스케일링 적용.
  const [popupFullscreenMap, setPopupFullscreenMap] = useState({});

  // 시계열 X축 표시 방식 — 'auto' (기본, 패널 자율) | 'sync' (모든 시계열 패널
  // 같은 tick 위치/포맷 + cross-panel hover 동기화). 상단 셀렉터로 토글.
  const [displayMode, setDisplayMode] = useState('auto');

  // 탭별 데이터 갱신 트리거 (auto-refresh가 fire되면 증가 → 하위 TabContent가 리로드)
  const [refreshTick, setRefreshTick] = useState(0);
  const autoRefreshTimerRef = useRef(null);

  // 탭 단위 변수 값 (런타임 only — 모달 닫으면 리셋).
  // shape: { [tabId]: { [varName]: value } }
  // 어느 패널이 publishVar 로 발행한 값을, 같은 탭의 다른 패널이 SQL :var 로 구독.
  const [tabVariables, setTabVariables] = useState({});

  const API_BASE = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/sql-developer\/?$/, '');

  // 전역 auto-refresh 타이머 → refreshTick 증가
  useEffect(() => {
    if (autoRefreshTimerRef.current) clearInterval(autoRefreshTimerRef.current);
    if (!autoRefresh) return;
    autoRefreshTimerRef.current = setInterval(() => setRefreshTick(t => t + 1), refreshInterval * 1000);
    return () => { if (autoRefreshTimerRef.current) clearInterval(autoRefreshTimerRef.current); };
  }, [autoRefresh, refreshInterval]);

  // 탭/로테이션 로드
  const loadPanels = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/sql-developer/monitoring/panels`);
      if (!res.ok) return;
      const json = await res.json();
      const t = (json.data?.tabs || []).filter(Boolean);
      setTabs(t);
      setRotation({ transition: 'fade', ...(json.data?.rotation || { enabled: false, dwellSec: 10 }) });
    } catch (err) {
      console.error('Failed to load panels:', err);
    }
  }, [API_BASE]);

  const loadInstances = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/sql-developer/monitoring/instances`);
      if (!res.ok) return;
      const data = await res.json();
      const list = data.data || [];
      setInstances(list);
      // 기본값은 등록된 첫 대상 — 고른 것이 사라졌을 때도
      setTargetInstanceId(prev => (list.some(i => i.registered && i.instanceId === prev) ? prev : ((list.find(i => i.registered) || {}).instanceId || '')));
    } catch {}
  }, [API_BASE]);

  const loadDatasources = useCallback(async () => {
    try {
      // 읽을 곳 목록 — 수집 저장소 + 연결(2026-10-18, 데이터소스를 따로 두지 않는다)
      const res = await fetch(`${API_BASE}/sql-developer/monitoring/sources`);
      if (!res.ok) return;
      const json = await res.json();
      setDatasources(json.data || []);
    } catch (err) {
      console.error('[MonitoringDashboardModal] sources load failed:', err);
    }
  }, [API_BASE]);

  const loadQueryTemplates = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/sql-developer/monitoring/query-templates`);
      if (!res.ok) return;
      const json = await res.json();
      setQueryTemplates(json.data || []);
    } catch (err) {
      console.error('[MonitoringDashboardModal] QueryTemplate 로드 실패:', err);
    }
  }, [API_BASE]);

  useEffect(() => {
    if (isOpen) {
      loadPanels();
      loadInstances();
      loadDatasources();
      loadQueryTemplates();
    }
  }, [isOpen, loadPanels, loadInstances, loadDatasources, loadQueryTemplates]);

  // 열려 있는 동안 대상 목록을 다시 읽는다(2026-10-28 신고) — 열 때 한 번만 읽어, 대시보드를 연 채 마법사로 등록하면
  // 마법사가 만든 탭은 그래프가 차는데 대상 목록은 옛것이라 「등록된 수집이 없어」 가 뜨고 기본 탭(전체 세션 수 …)은 대상이 비어 빈 그래프였다
  useEffect(() => {
    if (!isOpen) return undefined;
    const id = setInterval(loadInstances, 30000);
    const onFocus = () => loadInstances();
    window.addEventListener('focus', onFocus);
    return () => { clearInterval(id); window.removeEventListener('focus', onFocus); };
  }, [isOpen, loadInstances]);

  // 탭 순환 — 감춰진 탭과 분리된 탭은 순환 대상에서 제외. **탭 줄에 보이는 탭만** 돈다(2026-10-17): 내장 탭은 고른 대상의 DBMS 것만
  // 탭 줄에 보이는데(아래 tabFitsTarget), 순환은 그것을 거르지 않아 다른 DBMS 의 내장 탭(기본으로 순환 체크됨) 차례가 되면 화면이 그 탭을
  // 찾지 못하고 첫 탭(「설정」)을 보였다 — 체크하지 않은 화면이 순환에 끼어든 것처럼 보였다
  // 고른 대상의 DBMS 에 맞는 탭 — 탭 줄·순환·설정 목록이 **같은 판정**을 쓴다(아래 tabFitsTarget 과 같다)
  const fitTabs = useMemo(() => {
    const target = (instances || []).find(i => i.registered && i.instanceId === targetInstanceId);
    const dbType = (target && target.dbType) || 'oracle';
    return tabs.filter(x => x.kind !== 'builtin' || (x.dbType || 'oracle') === dbType);
  }, [tabs, instances, targetInstanceId]);
  const rotatableTabs = useMemo(
    () => byRotationOrder(fitTabs.filter(t => t.includeInRotation && !t.hidden && !detachedTabIds.has(t.id)), rotation.order),
    [fitTabs, detachedTabIds, rotation.order],
  );
  // 설정 탭의 「순환」 칸 — 탭 줄에 보이는 화면 중 체크한 것을 순환 순서대로. 다른 DBMS 의 내장 탭(탭 줄에 없다)을 세면
  // 탭 줄은 5개인데 순번이 7번까지 나왔다(2026-10-18 신고). 분리한 화면은 번호에 남긴다(되돌리면 그 자리로 돈다)
  const rotationList = useMemo(
    () => byRotationOrder(fitTabs.filter(t => t.includeInRotation && !t.hidden && !SYNTHETIC_TAB_IDS.has(t.id)), rotation.order),
    [fitTabs, rotation.order],
  );

  // activeTabId 가 바뀔 때마다 localStorage 에 보존 — 다음 모달 오픈 시 복원.
  useEffect(() => {
    if (!activeTabId) return;
    try { localStorage.setItem(STORAGE_KEY_LAST_TAB, activeTabId); } catch { /* ignore */ }
  }, [activeTabId]);

  // tabs 로드 후 보정: localStorage 에서 가져온 activeTabId 가 현재 visible 탭에
  // 없으면 (삭제/숨김 등) 첫 번째 비합성 탭으로 보정. 단 합성 탭(설정) 은 항상
  // 유효하므로 그대로 둠.
  useEffect(() => {
    if (!isOpen) return;
    if (!Array.isArray(tabs) || tabs.length === 0) return;
    if (SYNTHETIC_TAB_IDS.has(activeTabId)) return;   // 합성 탭은 항상 유효
    const visibleIds = new Set(tabs.filter(t => !t.hidden).map(t => t.id));
    if (!visibleIds.has(activeTabId)) {
      const firstReal = tabs.find(t => !t.hidden);
      if (firstReal) setActiveTabId(firstReal.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabs, isOpen]);

  useEffect(() => {
    if (rotationTimerRef.current) clearInterval(rotationTimerRef.current);
    if (!rotation.enabled || rotationPaused || rotatableTabs.length < 2) return;
    // 합성 탭(설정/데이터소스) 활성 중에는 로테이션 일시정지 — 작업 방해 방지
    if (SYNTHETIC_TAB_IDS.has(activeTabId)) return;
    rotationTimerRef.current = setInterval(() => {
      rotatedRef.current = true;   // 이 화면 바꿈은 순환이 했다 — 효과는 이때만(손으로 탭을 누르면 곧바로 바뀐다)
      setActiveTabId(prev => {
        const ids = rotatableTabs.map(t => t.id);
        if (ids.length === 0) return prev;
        const idx = ids.indexOf(prev);
        const next = idx < 0 ? ids[0] : ids[(idx + 1) % ids.length];
        return next;
      });
    }, Math.max(3, rotation.dwellSec) * 1000);
    return () => { if (rotationTimerRef.current) clearInterval(rotationTimerRef.current); };
  }, [rotation, rotationPaused, rotatableTabs, activeTabId]);

  // 순환이 화면을 바꾸면 나가는 화면·들어오는 화면의 짝을 만든다(손으로 탭을 누른 것은 곧바로 바뀐다).
  // **칠하기 전에**(useLayoutEffect) 정한다 — useEffect 면 새 화면이 다 보이는 한 장면이 먼저 칠해지고, 그 뒤 효과가 투명에서 시작해
  // 「새 화면 → 앞 화면 → 새 화면」 으로 깜박였다(2026-10-18 신고)
  useLayoutEffect(() => {
    const from = prevShownRef.current;
    prevShownRef.current = activeTabId;
    if (!rotatedRef.current) return;
    rotatedRef.current = false;
    if (!rotation.transition || rotation.transition === 'none' || !ROTATION_EFFECTS[rotation.transition]) return;
    setSwap(prev => ({ from: from !== activeTabId ? from : null, to: activeTabId, n: ((prev && prev.n) || 0) + 1 }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTabId]);
  // 짝이 그려진 직후(칠하기 전) 두 층을 함께 움직인다 — 끝나면 나가는 층을 내린다
  useLayoutEffect(() => {
    if (!swap) return undefined;
    const fx = ROTATION_EFFECTS[rotation.transition];
    const inEl = layerRefs.current.get(swap.to);
    const outEl = swap.from ? layerRefs.current.get(swap.from) : null;
    const view = (inEl && inEl.ownerDocument && inEl.ownerDocument.defaultView) || window;
    const reduce = view.matchMedia && view.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (!fx || !inEl || typeof inEl.animate !== 'function' || reduce) { setSwap(null); return undefined; }
    if (fx.mosaic) {
      // 가림막을 매 프레임 바꾼다 — 칠하기 전에 첫 가림막(아무것도 안 보임)을 씌워 새 화면이 한 번 번쩍이지 않게
      const starts = mosaicStarts(swap.n);
      const setMask = (v) => { inEl.style.maskImage = v; inEl.style.webkitMaskImage = v; inEl.style.maskSize = '100% 100%'; inEl.style.webkitMaskSize = '100% 100%'; };
      setMask(mosaicMask(starts, 0));
      const t0 = view.performance.now();
      let raf = 0;
      const clear = () => { inEl.style.maskImage = ''; inEl.style.webkitMaskImage = ''; inEl.style.maskSize = ''; inEl.style.webkitMaskSize = ''; };
      const step = (now) => {
        const p = Math.min(1, (now - t0) / ROTATION_EFFECT_MS);
        if (p >= 1) { clear(); setSwap(cur => (cur && cur.n === swap.n ? null : cur)); return; }
        setMask(mosaicMask(starts, p));
        raf = view.requestAnimationFrame(step);
      };
      raf = view.requestAnimationFrame(step);
      const guard = setTimeout(() => { view.cancelAnimationFrame(raf); clear(); setSwap(cur => (cur && cur.n === swap.n ? null : cur)); }, ROTATION_EFFECT_MS + 400);
      return () => { clearTimeout(guard); view.cancelAnimationFrame(raf); clear(); };
    }
    const opts = { duration: ROTATION_EFFECT_MS, easing: ROTATION_EASING, fill: 'both' };
    // animate() 가 애니메이션을 돌려주지 않는 환경이 있다(사용자 브라우저에서 null — 「Cannot set properties of null (setting 'onfinish')」,
    // 2026-10-18 신고). 그때는 효과 없이 넘어가되 화면은 멈추지 않게 — 아래 guard 타이머가 나가는 층을 내린다
    const play = (el, frames) => { try { return el.animate(frames, opts) || null; } catch { return null; } };
    const anims = [play(inEl, fx.in), outEl && fx.out ? play(outEl, fx.out) : null].filter(Boolean);
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      anims.forEach(a => { try { a.cancel(); } catch { /* 이미 끝남 */ } });
      setSwap(cur => (cur && cur.n === swap.n ? null : cur));
    };
    if (anims[0]) anims[0].onfinish = finish;
    const guard = setTimeout(finish, ROTATION_EFFECT_MS + 300);   // 창이 가려져 애니메이션이 멈춰도 층이 남지 않게
    return () => { clearTimeout(guard); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [swap && swap.n]);

  const saveRotation = async (next) => {
    setRotation(next);
    try {
      await fetch(`${API_BASE}/sql-developer/monitoring/panels/rotation`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(next)
      });
    } catch {}
  };
  // 순환 순서 바꾸기 — 설정 탭의 ▲▼. 순서는 체크한 화면 전부의 id 목록으로 저장한다(빠진 것은 뒤에 탭 순서대로)
  const handleMoveRotation = (tabId, dir) => {
    const ids = rotationList.map(x => x.id);
    const i = ids.indexOf(tabId);
    const j = i + dir;
    if (i < 0 || j < 0 || j >= ids.length) return;
    [ids[i], ids[j]] = [ids[j], ids[i]];
    saveRotation({ ...rotation, order: ids });
  };

  const handleToggleRotation = async () => {
    const next = { ...rotation, enabled: !rotation.enabled };
    // 멈추면 **지금 보이는 화면**에 머문다(처음 화면으로 돌아가지 않게)
    if (!next.enabled && shownTabIdRef.current) setActiveTabId(shownTabIdRef.current);
    // 시작하면 **곧바로 첫 화면부터**(2026-10-18 — 설정 탭에서 누르면 설정 탭에 머물러 반응이 없어 보였다. 설정 탭에서는 순환이 쉬고 있었다)
    if (next.enabled) {
      if (rotatableTabs.length === 0) { toast.info(t('monitoring:dash.rotateNone')); return; }
      rotatedRef.current = true;
      setActiveTabId(rotatableTabs[0].id);
    }
    setRotationPaused(false);
    setRotation(next);
    try {
      await fetch(`${API_BASE}/sql-developer/monitoring/panels/rotation`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(next)
      });
    } catch {}
  };

  const handleTransitionChange = async (transition) => {
    const next = { ...rotation, transition };
    setRotation(next);
    try {
      await fetch(`${API_BASE}/sql-developer/monitoring/panels/rotation`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(next)
      });
    } catch {}
  };

  const handleDwellChange = async (sec) => {
    const next = { ...rotation, dwellSec: sec };
    setRotation(next);
    try {
      await fetch(`${API_BASE}/sql-developer/monitoring/panels/rotation`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(next)
      });
    } catch {}
  };

  const handleToggleInclude = async (tabId) => {
    const tab = tabs.find(t => t.id === tabId);
    if (!tab) return;
    const next = { ...tab, includeInRotation: !tab.includeInRotation };
    setTabs(tabs.map(t => t.id === tabId ? next : t));
    try {
      await fetch(`${API_BASE}/sql-developer/monitoring/panels/tabs/${encodeURIComponent(tabId)}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ includeInRotation: next.includeInRotation })
      });
    } catch {}
  };

  const handleDetachTab = (tabId) => {
    const next = new Set(detachedTabIds);
    next.add(tabId);
    setDetachedTabIds(next);
    // 분리 중인 탭이 활성이면 다른 탭으로
    if (activeTabId === tabId) {
      const nextTab = tabs.find(t => !next.has(t.id));
      if (nextTab) setActiveTabId(nextTab.id);
    }
  };

  const handleAttachTab = (tabId) => {
    const next = new Set(detachedTabIds);
    next.delete(tabId);
    setDetachedTabIds(next);
  };

  const openEditor = (tabId, panel = null, slotIndex = null) =>
    setEditorState({ open: true, tabId, panel, slotIndex });
  const closeEditor = () =>
    setEditorState({ open: false, tabId: null, panel: null, slotIndex: null });

  const savePanel = async (panelDraft) => {
    const { tabId, panel } = editorState;
    if (!tabId) return;
    try {
      const isNew = !panel?.id;
      const url = isNew
        ? `${API_BASE}/sql-developer/monitoring/panels/tabs/${encodeURIComponent(tabId)}/panels`
        : `${API_BASE}/sql-developer/monitoring/panels/tabs/${encodeURIComponent(tabId)}/panels/${encodeURIComponent(panel.id)}`;
      const res = await fetch(url, {
        method: isNew ? 'POST' : 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(panelDraft)
      });
      if (!res.ok) {
        const msg = await describeHttpError(res, t('monitoring:dash.err.panelSave'), t);
        toast.error(msg);
        return;
      }
      await loadPanels();
      closeEditor();
      setRefreshTick(t => t + 1);
      toast.success(isNew ? t('monitoring:dash.toast.panelAdded') : t('monitoring:dash.toast.panelSaved'));
    } catch (err) {
      toast.error(t('monitoring:dash.err.panelSaveMsg', { message: err.message }));
    }
  };

  // 패널의 차트 요소 클릭 시 호출. producer 패널이면 produces 설정에 따라
  // 값 추출 → tabVariables 갱신 → 같은 탭의 consumer 패널들이 자동 재페치.
  const handlePanelElementClick = useCallback((tabId, panel, datum) => {
    const pv = panel?.produces;
    if (!pv?.enabled || !pv.name) return;
    const value =
        pv.extract === 'y'      ? datum.y
      : pv.extract === 'series' ? datum.series
      : datum.x;   // default 'x'
    if (value === undefined || value === null) return;
    setTabVariables(prev => ({
      ...prev,
      [tabId]: { ...(prev[tabId] || {}), [pv.name]: value },
    }));
  }, []);

  // 탭 패널 목록이 바뀌면 producer 의 initialValue 로 변수 시드.
  // 기존에 (클릭으로) 갱신된 값은 그대로 유지 — 이름이 신규일 때만 채움.
  useEffect(() => {
    if (!Array.isArray(tabs) || tabs.length === 0) return;
    setTabVariables(prev => {
      const next = { ...prev };
      let mutated = false;
      for (const tab of tabs) {
        const current = { ...(prev[tab.id] || {}) };
        for (const p of (tab.panels || [])) {
          if (p.produces?.enabled && p.produces?.name) {
            if (!(p.produces.name in current)) {
              current[p.produces.name] = p.produces.initialValue ?? '';
              mutated = true;
            }
          }
        }
        // 더 이상 producer 가 없는 변수는 정리 — stale 값 제거
        const validNames = new Set(
          (tab.panels || [])
            .filter(p => p.produces?.enabled && p.produces?.name)
            .map(p => p.produces.name)
        );
        for (const k of Object.keys(current)) {
          if (!validNames.has(k)) {
            delete current[k];
            mutated = true;
          }
        }
        next[tab.id] = current;
      }
      return mutated ? next : prev;
    });
  }, [tabs]);

  // 패널 드래그 이동 — source 패널의 slotIndex 를 targetSlotIndex 로 변경.
  // target 자리가 이미 점유돼 있으면 해당 패널과 slotIndex 를 swap (2 PUT).
  const handlePanelReorder = useCallback(async (tabId, sourcePanelId, targetSlotIndex) => {
    const tab = tabs.find(t => t.id === tabId);
    if (!tab) return;
    const source = (tab.panels || []).find(p => p.id === sourcePanelId);
    if (!source) return;
    const sourceSlot = typeof source.slotIndex === 'number' ? source.slotIndex : -1;
    if (sourceSlot === targetSlotIndex) return;   // 같은 자리 — no-op
    const target = (tab.panels || []).find(p => typeof p.slotIndex === 'number' && p.slotIndex === targetSlotIndex);

    const putPanel = (pid, patch) =>
      fetch(`${API_BASE}/sql-developer/monitoring/panels/tabs/${encodeURIComponent(tabId)}/panels/${encodeURIComponent(pid)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(patch),
      });

    try {
      // 충돌 회피: source 먼저 임시 슬롯(음수)으로 이동 → target 이 있으면 source 원래 자리로 이동 → source 를 target 자리로
      // 간단 2 PUT 버전 사용 (타겟 빈 슬롯: 1 PUT, 점유 슬롯: 2 PUT swap).
      if (target) {
        const res1 = await putPanel(target.id, { slotIndex: sourceSlot });
        if (!res1.ok) { toast.error(await describeHttpError(res1, t('monitoring:dash.err.panelMove'), t)); return; }
      }
      const res2 = await putPanel(source.id, { slotIndex: targetSlotIndex });
      if (!res2.ok) { toast.error(await describeHttpError(res2, t('monitoring:dash.err.panelMove'), t)); return; }
      await loadPanels();
      setRefreshTick(t => t + 1);
    } catch (err) {
      toast.error(t('monitoring:dash.err.panelMoveMsg', { message: err.message }));
    }
  }, [API_BASE, tabs, loadPanels]);

  // 패널 활성/비활성 토글 — 카드 헤더 토글 버튼에서 호출.
  // 비활성 시 PanelTabContent 의 fetch 루프가 스킵하므로 쿼리 실행 안 함.
  const togglePanelEnabled = useCallback(async (tabId, panel) => {
    const next = !(panel.enabled !== false);   // undefined/true → false, false → true
    try {
      const res = await fetch(
        `${API_BASE}/sql-developer/monitoring/panels/tabs/${encodeURIComponent(tabId)}/panels/${encodeURIComponent(panel.id)}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ enabled: next }),
        }
      );
      if (!res.ok) {
        toast.error(await describeHttpError(res, t('monitoring:dash.err.panelToggle'), t));
        return;
      }
      await loadPanels();
      setRefreshTick(t => t + 1);
    } catch (err) {
      toast.error(t('monitoring:dash.err.panelToggleMsg', { message: err.message }));
    }
  }, [API_BASE, loadPanels]);

  // PanelEditor 의 onDelete 는 확인을 이미 PanelEditor 자체가 담당하지 않으므로
  // 여기서 toast 기반 확인 대신 react-toastify promise 패턴을 쓸 수도 있지만,
  // 파괴적 작업이라 window.confirm 은 명시적 사용자 동의라는 의미로 유지.
  const deletePanel = async (panelToDelete) => {
    const { tabId } = editorState;
    if (!tabId || !panelToDelete?.id) return;
    const ok = await confirm({
      title: t('monitoring:dash.deletePanel'),
      message: t('monitoring:dash.confirm.deletePanel'),
      confirmText: t('monitoring:dash.delete'),
      variant: 'danger',
    });
    if (!ok) return;
    try {
      const res = await fetch(
        `${API_BASE}/sql-developer/monitoring/panels/tabs/${encodeURIComponent(tabId)}/panels/${encodeURIComponent(panelToDelete.id)}`,
        { method: 'DELETE' }
      );
      if (!res.ok) {
        const msg = await describeHttpError(res, t('monitoring:dash.err.panelDelete'), t);
        toast.error(msg);
        return;
      }
      await loadPanels();
      closeEditor();
      setRefreshTick(t => t + 1);
      toast.success(t('monitoring:dash.toast.panelDeleted'));
    } catch (err) {
      toast.error(t('monitoring:dash.err.panelDeleteMsg', { message: err.message }));
    }
  };

  // 탭의 고정 그리드 레이아웃 변경 (설정 탭에서 호출)
  // layout === null 이면 "자동"(auto-fit) 으로 복귀.
  const handleChangeLayout = useCallback(async (tabId, layout) => {
    if (tabId === SETTINGS_TAB_ID) return;
    // 로컬 state 즉시 갱신 → UI 반응성 확보
    setTabs(prev => prev.map(t => {
      if (t.id !== tabId) return t;
      if (layout === null) {
        const next = { ...t };
        delete next.layout;
        return next;
      }
      return { ...t, layout };
    }));
    try {
      const res = await fetch(`${API_BASE}/sql-developer/monitoring/panels/tabs/${encodeURIComponent(tabId)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ layout }),
      });
      if (!res.ok) {
        const msg = await describeHttpError(res, t('monitoring:dash.err.layout'), t);
        toast.error(msg);
      }
    } catch (err) {
      console.error('[MonitoringDashboardModal] layout 변경 실패:', err);
      toast.error(t('monitoring:dash.err.layoutMsg', { message: err.message }));
    }
  }, [API_BASE]);

  // 전체화면 토글
  const toggleFullscreen = useCallback(() => setIsFullscreen(v => !v), []);
  const handleClose = useCallback(() => {
    setIsFullscreen(false);  // 다음 오픈 시 기본 크기로 시작
    onClose?.();
  }, [onClose]);

  // 사용자 정의 탭 생성 (설정 탭의 "+ 탭 추가" 에서 호출)
  const handleCreateTab = useCallback(async (name) => {
    try {
      const res = await fetch(`${API_BASE}/sql-developer/monitoring/panels/tabs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name }),
      });
      if (!res.ok) {
        const msg = await describeHttpError(res, t('monitoring:dash.err.tabAdd'), t);
        toast.error(msg);
        return;
      }
      await loadPanels();
      toast.success(t('monitoring:dash.toast.tabAdded', { name: name }));
    } catch (err) {
      console.error('[MonitoringDashboardModal] 탭 생성 실패:', err);
      toast.error(t('monitoring:dash.err.tabAddMsg', { message: err.message }));
    }
  }, [API_BASE, loadPanels]);

  // 사용자 정의 탭 삭제 (user kind 만 가능)
  const handleDeleteTab = useCallback(async (tabId) => {
    try {
      const res = await fetch(
        `${API_BASE}/sql-developer/monitoring/panels/tabs/${encodeURIComponent(tabId)}`,
        { method: 'DELETE' }
      );
      if (!res.ok) {
        const msg = await describeHttpError(res, t('monitoring:dash.err.tabDelete'), t);
        toast.error(msg);
        return;
      }
      // 삭제된 탭이 현재 활성이면 개요로 이동
      setActiveTabId(prev => (prev === tabId ? 'overview' : prev));
      // detach 상태였다면 해제
      setDetachedTabIds(prev => {
        if (!prev.has(tabId)) return prev;
        const next = new Set(prev);
        next.delete(tabId);
        return next;
      });
      await loadPanels();
      toast.success(t('monitoring:dash.toast.tabDeleted'));
    } catch (err) {
      console.error('[MonitoringDashboardModal] 탭 삭제 실패:', err);
      toast.error(t('monitoring:dash.err.tabDeleteMsg', { message: err.message }));
    }
  }, [API_BASE, loadPanels]);

  // 탭 이름 변경 (현재 UI 에서는 사용자 탭만 노출하지만, 엔드포인트는 name 업데이트를 동일하게 지원)
  const handleRenameTab = useCallback(async (tabId, name) => {
    const trimmed = String(name || '').trim();
    if (!trimmed) return;
    // 로컬 즉시 반영
    setTabs(prev => prev.map(t => t.id === tabId ? { ...t, name: trimmed } : t));
    try {
      const res = await fetch(`${API_BASE}/sql-developer/monitoring/panels/tabs/${encodeURIComponent(tabId)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: trimmed }),
      });
      if (!res.ok) {
        const msg = await describeHttpError(res, t('monitoring:dash.err.tabRename'), t);
        toast.error(msg);
        await loadPanels();
      }
    } catch (err) {
      console.error('[MonitoringDashboardModal] 탭 이름 변경 실패:', err);
      toast.error(t('monitoring:dash.err.tabRenameMsg', { message: err.message }));
      // 실패 시 재로드로 서버 상태와 동기화
      await loadPanels();
    }
  }, [API_BASE, loadPanels]);

  // 탭 숨기기/보이기 토글 (설정 탭에서만 제어 — 탭 바에는 감춘 탭이 아예 안 보임)
  const handleToggleHidden = useCallback(async (tabId, hidden) => {
    // 로컬 즉시 반영
    setTabs(prev => prev.map(t => t.id === tabId ? { ...t, hidden: !!hidden } : t));
    // 감춤으로 전환되는 탭이 현재 활성이면 개요로 이동
    if (hidden && activeTabId === tabId) {
      setActiveTabId('overview');
    }
    try {
      const res = await fetch(`${API_BASE}/sql-developer/monitoring/panels/tabs/${encodeURIComponent(tabId)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ hidden: !!hidden }),
      });
      if (!res.ok) {
        const msg = await describeHttpError(res, t('monitoring:dash.err.tabHide'), t);
        toast.error(msg);
        await loadPanels();
      }
    } catch (err) {
      console.error('[MonitoringDashboardModal] 탭 숨김 상태 변경 실패:', err);
      toast.error(t('monitoring:dash.err.tabHideMsg', { message: err.message }));
      await loadPanels();
    }
  }, [API_BASE, activeTabId, loadPanels]);

  // DataSource CRUD 핸들러는 여기서 제거. 관리는 최상위 "모니터링 > 데이터소스" 메뉴의
  // DataSourceManagerModal 에서 전담. 대시보드는 DS 를 "참조" 만 한다.

  // PanelEditor 의 "템플릿으로 저장" 에서 호출. 사용자 템플릿 생성.
  const handleSaveQueryTemplate = useCallback(async (tplDraft) => {
    try {
      const res = await fetch(`${API_BASE}/sql-developer/monitoring/query-templates`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(tplDraft),
      });
      if (!res.ok) {
        toast.error(await describeHttpError(res, t('monitoring:dash.err.templateSave'), t));
        return;
      }
      await loadQueryTemplates();
      toast.success(t('monitoring:dash.toast.templateAdded', { name: tplDraft.name }));
    } catch (err) {
      toast.error(t('monitoring:dash.err.templateSaveMsg', { message: err.message }));
    }
  }, [API_BASE, loadQueryTemplates]);

  // 모든 탭 설정을 서버에 재동기화 (설정 탭의 "설정 저장" 버튼에서 호출).
  // 각 탭 조작은 이미 auto-save 되지만, 네트워크 실패·부분 저장 등에 대비해
  // 한번에 전부 PUT 으로 재전송하고 성공 토스트로 사용자에게 확인을 제공.
  const handleSaveAll = useCallback(async () => {
    const syncable = tabs.filter(tb => tb && tb.kind !== 'overview' && tb.canEdit !== false);
    if (syncable.length === 0) {
      toast.info(t('monitoring:dash.toast.nothingToSave'));
      return;
    }
    const results = await Promise.allSettled(syncable.map(t => {
      const payload = {
        name: t.name,
        includeInRotation: !!t.includeInRotation,
        hidden: !!t.hidden,
        layout: t.layout === undefined ? null : t.layout,
      };
      return fetch(`${API_BASE}/sql-developer/monitoring/panels/tabs/${encodeURIComponent(t.id)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      }).then(res => { if (!res.ok) throw new Error(`HTTP ${res.status}`); });
    }));
    const failed = results.filter(r => r.status === 'rejected');
    if (failed.length === 0) {
      toast.success(t('monitoring:dash.toast.settingsSaved', { count: syncable.length }));
    } else if (failed.length < results.length) {
      toast.warning(t('monitoring:dash.toast.partialSave', { failed: failed.length, total: results.length }));
    } else {
      toast.error(t('monitoring:dash.toast.settingsFailed'));
    }
    // 최종 상태 재동기화
    await loadPanels();
  }, [API_BASE, tabs, loadPanels]);

  const isDarkMode = document.documentElement.getAttribute('data-theme') === 'dark';

  // 창처럼 — 끌어 이동 · 최대화 · 새 창으로 분리(다른 모니터에서 전체 화면). common/useModalWindow (2026-10-06)
  const win = useModalWindow({ title: t('monitoring:dash.title'), maximized: isFullscreen, onToggleMaximize: () => setIsFullscreen(v => !v) });
  const { reset: resetWin } = win;
  useEffect(() => { if (!isOpen) resetWin(); }, [isOpen, resetWin]);   // 닫으면 다음엔 제자리·앱 안에서

  if (!isOpen) return null;

  // 맨 앞에 합성 설정 탭 주입 (백엔드 미전송).
  // 탭 바에서는 숨김 처리된 탭 제외. 단 SettingsTab 에는 감춘 탭 관리용 원본 tabs 를 전달.
  // 내장 탭은 **고른 대상의 DBMS** 것만 — 수집 프로파일마다 지표가 달라 다른 DBMS 의 탭은 비어 보인다(2026-10-04, 다중 DBMS M3).
  // dbType 이 없는 내장 탭은 예전부터 있던 Oracle 탭이다. 사용자 탭·사용자 기준 탭은 DBMS 와 무관하게 보인다
  const registeredInstances = instances.filter(i => i.registered);
  const selectedDbType = ((registeredInstances.find(i => i.instanceId === targetInstanceId) || {}).dbType) || 'oracle';
  const tabFitsTarget = (x) => x.kind !== 'builtin' || (x.dbType || 'oracle') === selectedDbType;
  const visibleTabs = [getSettingsTab(t), ...tabs.filter(x => !x.hidden && tabFitsTarget(x))];
  const activeTab = visibleTabs.find(t => t.id === activeTabId) || visibleTabs[0];
  shownTabIdRef.current = activeTab ? activeTab.id : null;
  const isSyntheticActive = activeTab && SYNTHETIC_TAB_IDS.has(activeTab.id);
  // 순환 무대의 층 — 지금 화면, 전환 중이면 나가는 화면, 순환이 돌고 있으면 다음 화면(미리 그려 데이터를 받아 둔다)
  const stageLayers = (() => {
    if (!activeTab || isSyntheticActive) return [];
    const out = [{ tab: activeTab, role: 'active' }];
    const byId = (id) => tabs.find(x => x.id === id);
    // 나가는 화면 — 순환이 화면을 바꾼 **첫 렌더**부터 남긴다. 짝(swap)은 그 뒤 효과에서 정해지므로 그것만 보면 한 렌더 동안 나가는 층이
    // 목록에서 빠졌다가 다시 들어와(언마운트 → 새로 마운트) 빈 화면으로 밀려났다(화면 확인 2026-10-18). 그래서 아직 정해지기 전의 바뀌기 전 화면도 본다
    const pendingFrom = rotatedRef.current && prevShownRef.current && prevShownRef.current !== activeTab.id ? prevShownRef.current : null;
    const fromId = (swap && swap.to === activeTab.id && swap.from) || pendingFrom;
    if (fromId && fromId !== activeTab.id && !SYNTHETIC_TAB_IDS.has(fromId)) {
      const from = byId(fromId);
      if (from) out.push({ tab: from, role: 'leaving' });
    }
    if (rotation.enabled && !rotationPaused && rotatableTabs.length > 1 && preloadFor === activeTab.id) {
      const ids = rotatableTabs.map(x => x.id);
      const i = ids.indexOf(activeTab.id);
      const next = rotatableTabs[(i < 0 ? 0 : i + 1) % ids.length];
      if (next && !out.some(o => o.tab.id === next.id)) out.push({ tab: next, role: 'preload' });
    }
    // 그리는 순서는 **탭 순서로 고정**한다(쌓임은 역할별 z-index 가 정한다). 역할 순서로 그리면 역할이 바뀔 때마다 React 가 층 요소를
    // DOM 에서 옮기고, 그 순간 차트(크기 맞춤 그릇)가 다시 재면서 비었다가 그려져 나가는 화면이 0.5초쯤 빈 채로 밀려났다(화면 확인 2026-10-18)
    const order = new Map(tabs.map((x, i) => [x.id, i]));
    return out.sort((a, b) => (order.get(a.tab.id) ?? 0) - (order.get(b.tab.id) ?? 0));
  })();

  const activeIsDetached = activeTab && !isSyntheticActive && detachedTabIds.has(activeTab.id);

  const fixedTarget = activeTab && activeTab.targetInstanceId ? activeTab.targetInstanceId : null;
  const effectiveInstanceId = fixedTarget || targetInstanceId || '';
  const tabContentProps = {
    API_BASE,
    onReload: loadPanels,
    demo: useDemo,
    instanceId: effectiveInstanceId,
    minutes: rangeMin || timeWindowMin,
    range: customRange,
    timeDomain: rangeDomain,
    // 순환 중 — 패널만 보인다(접힌 패널 줄·추가 줄 없음)
    presentation: !!rotation.enabled,
    isDarkMode,
    refreshTick,
    instances,
    tabs,
    settingsTabs: fitTabs,   // 설정 목록도 탭 줄과 같은 범위(감춘 탭은 관리하려고 남긴다)
    rotationList,
    onToggleRotationInclude: handleToggleInclude,
    onMoveRotation: handleMoveRotation,
    datasources,
    connections,
    onChangeLayout: handleChangeLayout,
    onCreateTab: handleCreateTab,
    onDeleteTab: handleDeleteTab,
    onRenameTab: handleRenameTab,
    onToggleHidden: handleToggleHidden,
    onSaveAll: handleSaveAll,
    onEditPanel: openEditor,
    // 순환 중에는 패널을 추가하지 않는다(2026-10-17) — 「+ 패널 추가」 를 감춘다. 몇 초 뒤 탭이 넘어가 버리는 화면에서 편집을 시작하게 두지 않는다
    onAddPanelAtSlot: rotation.enabled ? null : (tabId, slotIndex) => openEditor(tabId, null, slotIndex),
    onAddPanelAppend: rotation.enabled ? null : (tabId) => {
      // 신규 패널은 항상 접기 상태로 추가 — slotIndex >= layout slotCount 가 되도록 계산
      const SLOT_COUNT_LOCAL = { '1x1': 1, '1x2': 2, '2x1': 2, '2x2': 4, '1L2R': 3, '2L1R': 3 };
      const tab = tabs.find(t => t.id === tabId);
      const slotCount = SLOT_COUNT_LOCAL[tab?.layout] ?? 0;
      const maxIdx = (tab?.panels || [])
        .map(p => typeof p.slotIndex === 'number' ? p.slotIndex : -1)
        .reduce((a, b) => Math.max(a, b), -1);
      const collapsedIdx = Math.max(slotCount, maxIdx + 1);
      openEditor(tabId, null, collapsedIdx);
    },
    onTogglePanel: togglePanelEnabled,
    onReorderPanel: handlePanelReorder,
    onPanelElementClick: handlePanelElementClick,
    tabVariables,
    isFullscreen,   // 메인 모달 fullscreen 상태 — TabContent → ... → PanelChart 까지 전달
    displayMode,    // 시계열 X축 표시 방식 — 'auto' | 'sync'
  };

  return win.wrap(
    <div className={styles.overlay} style={win.overlayStyle}>
      <div className={`app-modal ${styles.modal} ${isFullscreen ? styles.modalFullscreen : ''}`} data-app-modal ref={win.frameRef} style={win.frameStyle}>
        <div className={`app-modal-header ${styles.header}`} {...win.headerProps}>
          <h2 className="app-modal-title"><FiActivity size={14} />{t('monitoring:dash.title')}</h2>
          <div className={styles.headerControls}>
            {/* 예시 데이터 — 세션 분석과 같은 방식: 켜면 모든 패널이 예시를(DB 에 가지 않는다). 기본 꺼짐 · 열 때마다 꺼짐 · 저장 안 함 */}
            <label className={styles.syncCheckLabel} title={t('monitoring:dash.demoHint')}>
              <input type="checkbox" checked={useDemo} onChange={(e) => setUseDemo(e.target.checked)} />
              {t('monitoring:dash.demoData')}
            </label>
            {/* 시계열 X축 동기화 — 체크 시 모든 시계열 패널이 같은 tick 위치/포맷
                + cross-panel hover sync. 기본 off = 현재 동작. */}
            <label
              className={styles.syncCheckLabel}
              title={t('monitoring:dash.syncHint')}
            >
              <input
                type="checkbox"
                checked={displayMode === 'sync'}
                onChange={(e) => setDisplayMode(e.target.checked ? 'sync' : 'auto')}
              />
              {t('monitoring:dash.syncTimeAxis')}
            </label>
            {/* 대상 DB — 탭에 고정된 대상이면 그것만 보인다 */}
            <select
              className={styles.connSelect}
              value={effectiveInstanceId}
              onChange={(e) => setTargetInstanceId(e.target.value)}
              disabled={!!fixedTarget || useDemo}
              title={fixedTarget ? t('monitoring:dash.targetFixedHint') : t('monitoring:dash.targetHint')}
            >
              {registeredInstances.length === 0 && <option value="">{t('monitoring:dash.noTarget')}</option>}
              {fixedTarget && !registeredInstances.some(i => i.instanceId === fixedTarget) && <option value={fixedTarget}>{fixedTarget}</option>}
              {registeredInstances.map(i => (
                <option key={i.instanceId} value={i.instanceId}>{i.displayName || i.instanceId}{i.dbType && i.dbType !== 'oracle' ? ` (${i.dbType === 'postgresql' ? 'PostgreSQL' : i.dbType})` : ''}</option>
              ))}
            </select>
            <NodeListLoader API_BASE={API_BASE} instanceId={effectiveInstanceId} demo={useDemo} onList={setNodeList} />
            <select
              className={styles.connSelect}
              value={customRange || rangeDraft ? 'custom' : timeWindowMin}
              onChange={(e) => {
                if (e.target.value === 'custom') {
                  // 처음 고르면 최근 1시간으로 채워 둔다 — 고친 뒤 「적용」 해야 조회가 바뀐다
                  const local = (d) => new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
                  const now = new Date();
                  setRangeDraft(customRange || { fromAt: local(new Date(now.getTime() - 3600000)), toAt: local(now) });
                  return;
                }
                setCustomRange(null); setRangeDraft(null);
                setTimeWindowMin(parseInt(e.target.value, 10));
              }}
              title={t('monitoring:dash.timeRange')}
            >
              <option value="10">{t('monitoring:dash.range.min10')}</option>
              <option value="30">{t('monitoring:dash.range.min30')}</option>
              <option value="60">{t('monitoring:dash.range.hour1')}</option>
              <option value="360">{t('monitoring:dash.range.hour6')}</option>
              <option value="1440">{t('monitoring:dash.range.day1')}</option>
              <option value="10080">{t('monitoring:dash.range.day7')}</option>
              <option value="custom">{t('monitoring:dash.range.custom')}</option>
            </select>
            {rangeDraft && (
              <span className={styles.rangeBox}>
                <input type="datetime-local" value={rangeDraft.fromAt} onChange={(e) => setRangeDraft(d => ({ ...d, fromAt: e.target.value }))} aria-label={t('monitoring:dash.range.from')} />
                <span>~</span>
                <input type="datetime-local" value={rangeDraft.toAt} onChange={(e) => setRangeDraft(d => ({ ...d, toAt: e.target.value }))} aria-label={t('monitoring:dash.range.to')} />
                <button type="button" className="app-btn"
                  disabled={!rangeDraft.fromAt || !rangeDraft.toAt || rangeDraft.toAt <= rangeDraft.fromAt}
                  onClick={() => setCustomRange({ ...rangeDraft })}>{t('monitoring:dash.range.apply')}</button>
              </span>
            )}
            <button className={`app-modal-iconbtn ${styles.refreshBtn}`} onClick={() => setRefreshTick(t => t + 1)} title={t('monitoring:dash.refresh')}>
              <FiRefreshCw size={14} />
            </button>
            {/* 앱 안 최대화 — 분리한 창에서는 뜻이 없다(그 창의 「전체 화면」 이 win.controls 에 있다). 둘 다 두면 같은 ⤢ 가 나란히 선다(2026-10-18 신고) */}
            {!win.popped && !win.placed && (
              <button
                className={`app-modal-iconbtn ${styles.fsBtn}`}
                onClick={toggleFullscreen}
                title={isFullscreen ? t('monitoring:dash.exitFullscreen') : t('monitoring:dash.fullscreen')}
              >
                {isFullscreen ? <FiMinimize2 size={14} /> : <FiMaximize2 size={14} />}
              </button>
            )}
            {win.controls}
            <button className={`app-modal-close ${styles.closeBtn}`} onClick={handleClose}>
              <FiX size={16} />
            </button>
          </div>
        </div>

        {/* 탭 바 (합성 탭: 설정·데이터소스 는 detach/rotation 컨트롤 감춤) */}
        <div className={styles.tabBar}>
          {visibleTabs.map(tab => {
            const isSynthetic = SYNTHETIC_TAB_IDS.has(tab.id);
            return (
              <div key={tab.id} className={`${styles.tabItem} ${tab.id === activeTabId ? styles.tabActive : ''}`}>
                <button
                  className={styles.tabMain}
                  onClick={() => setActiveTabId(tab.id)}
                  title={
                    tab.kind === 'settings' ? t('monitoring:dash.globalSettings')
                      : tab.kind === 'custom' ? t('monitoring:dash.customCriteria')
                      : tab.kind
                  }
                >
                  <span className={styles.tabIcon}>
                    {tab.kind === 'settings' && <FiSettings size={13} />}
                    {tab.kind === 'overview' && <FiBarChart2 size={13} />}
                    {tab.kind === 'builtin' && <FiLayers size={13} />}
                    {tab.kind === 'custom' && <FiUser size={13} />}
                    {tab.kind === 'user' && <FiBookmark size={13} />}
                  </span>
                  {tab.name}
                  {tab.subscribed && <FiShare2 size={11} style={{ marginLeft: 4, opacity: 0.7 }} title={t('monitoring:st.subscribedFrom', { name: (tab.owner && tab.owner.name) || '' })} />}
                  {!isSynthetic && detachedTabIds.has(tab.id) && (
                    <FiExternalLink size={11} style={{ marginLeft: 4, opacity: 0.6 }} />
                  )}
                </button>
                {!isSynthetic && (
                  <label className={styles.tabRotationLabel} title={t('monitoring:dash.rotateInclude')}>
                    <input
                      type="checkbox"
                      checked={!!tab.includeInRotation}
                      onChange={() => handleToggleInclude(tab.id)}
                    />
                  </label>
                )}
                {!isSynthetic && (!detachedTabIds.has(tab.id) ? (
                  <button className={styles.tabActionBtn} onClick={() => handleDetachTab(tab.id)} title={t('monitoring:dash.detach')}>
                    <FiExternalLink size={12} />
                  </button>
                ) : (
                  <button className={styles.tabActionBtn} onClick={() => handleAttachTab(tab.id)} title={t('monitoring:dash.reattach')}>
                    <FiLogIn size={12} />
                  </button>
                ))}
              </div>
            );
          })}
        </div>

        {/* 패널 탭은 스크롤 없이 한 화면에(2026-10-18 — 순환·관제 화면은 한눈에). 설정 같은 합성 탭은 내용이 길어 스크롤한다 */}
        <div className={`${styles.body} ${isSyntheticActive ? '' : styles.bodyFit}`}>
          {activeIsDetached ? (
            <div className={styles.detachedNotice}>
              <p>{t('monitoring:dash.tabDetached')}</p>
              <button className={styles.btnSecondary} onClick={() => handleAttachTab(activeTab.id)}>{t('monitoring:dash.reattach')}</button>
            </div>
          ) : activeTab ? (
            <>
              {/* 기본 탭은 분석 창들과 겹친다(2026-10-03 점검). 대시보드는 「진단 팩 없이 상시 수집·관제」 가 몫이므로
                  탭은 두되, 같은 내용을 더 정확히 보는 창으로 가는 길을 둔다 */}
              {/* 순환 중에는 감춘다 — 탭마다 있고 없어 화면 높이가 바뀌면 전환이 덜컹거린다 */}
              {RELATED_WINDOWS[activeTab.id] && onOpenWindow && !rotation.enabled && (
                <div className={styles.relatedBar}>
                  <span className={styles.relatedNote}>{t('monitoring:dash.related.note')}</span>
                  {RELATED_WINDOWS[activeTab.id].map(key => (
                    <button key={key} type="button" className={styles.relatedBtn} onClick={() => onOpenWindow(key)}>
                      {t(`monitoring:dash.related.${key}`)} <FiExternalLink size={11} />
                    </button>
                  ))}
                </div>
              )}
              {/* 수집이 하나도 등록되지 않았으면 그래프가 빌 수밖에 없다 — 그 사실과 가는 길을 말한다 */}
              {!useDemo && registeredInstances.length === 0 && !fixedTarget && !isSyntheticActive && (
                <div className={styles.collectBanner}>
                  <FiInfo size={14} />
                  <span>{t('monitoring:dash.noCollection')}</span>
                  {onOpenCollectionSettings && (
                    <button type="button" className="app-btn app-btn-primary" onClick={onOpenCollectionSettings}>{t('monitoring:dash.openSettings')}</button>
                  )}
                </div>
              )}
              {isSyntheticActive ? (
                <TabContent tab={activeTab} {...tabContentProps} />
              ) : (
                // 층 — 지금 화면 · (전환 중) 나가는 화면 · (순환 중) 다음 화면을 미리. 같은 탭은 늘 같은 key 라 다음 화면이 지금 화면이 될 때 다시 만들지 않는다
                <div className={styles.slideStage}>
                  {stageLayers.map(({ tab, role }) => (
                    <div
                      key={tab.id}
                      ref={(el) => { if (el) layerRefs.current.set(tab.id, el); else layerRefs.current.delete(tab.id); }}
                      className={`${styles.slideLayer} ${role === 'active' ? styles.slideLayerActive : role === 'leaving' ? styles.slideLayerLeaving : styles.slideLayerPreload}`}
                      aria-hidden={role !== 'active' ? true : undefined}
                    >
                      <TabContent tab={tab} {...tabContentProps} />
                    </div>
                  ))}
                </div>
              )}
            </>
          ) : (
            <div className={styles.noData}>{t('monitoring:dash.noTabs')}</div>
          )}
        </div>

        <div className={styles.footer}>
          <div className={styles.autoRefreshControl}>
            <label>
              <input
                type="checkbox"
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
              />
              {t('monitoring:dash.autoRefreshSec', { n: refreshInterval })}
            </label>
            {autoRefresh && (
              <select
                className={styles.intervalSelect}
                value={refreshInterval}
                onChange={(e) => setRefreshInterval(parseInt(e.target.value, 10))}
              >
                <option value="10">{t('monitoring:dash.every.sec10')}</option>
                <option value="30">{t('monitoring:dash.every.sec30')}</option>
                <option value="60">{t('monitoring:dash.every.min1')}</option>
              </select>
            )}
          </div>
          <div className={styles.rotationControl}>
            <button
              className={`${styles.btnSecondary} ${rotation.enabled ? styles.rotationOn : ''}`}
              onClick={handleToggleRotation}
              title={rotation.enabled ? t('monitoring:dash.rotateStop') : t('monitoring:dash.rotateStart')}
            >
              {rotation.enabled ? <FiSquare size={14} /> : <FiPlay size={14} />}
              <span style={{ marginLeft: 6 }}>{rotation.enabled ? t('monitoring:dash.rotateStop') : t('monitoring:dash.rotateStart')}</span>
            </button>
            {rotation.enabled && (
              <button
                className={`${styles.btnSecondary} ${rotationPaused ? '' : styles.rotationOn}`}
                onClick={() => setRotationPaused(p => !p)}
                title={rotationPaused ? t('monitoring:dash.rotateResumeHint') : t('monitoring:dash.rotatePauseHint')}
                aria-pressed={rotationPaused}
              >
                {rotationPaused ? <FiPlay size={14} /> : <FiPause size={14} />}
                <span style={{ marginLeft: 6 }}>{rotationPaused ? t('monitoring:dash.rotateResume') : t('monitoring:dash.rotatePause')}</span>
              </button>
            )}
            <label className={styles.dwellLabel} title={t('monitoring:dash.transitionHint')}>
              {t('monitoring:dash.transition')}
              <select
                className={styles.intervalSelect}
                value={rotation.transition || 'fade'}
                onChange={(e) => handleTransitionChange(e.target.value)}
              >
                {Object.keys(ROTATION_EFFECTS).concat('none').map(k => (
                  <option key={k} value={k}>{t(`monitoring:dash.fx.${k}`)}</option>
                ))}
              </select>
            </label>
            <label className={styles.dwellLabel}>
              {t('monitoring:dash.dwellTime')}
              <select
                className={styles.intervalSelect}
                value={rotation.dwellSec}
                onChange={(e) => handleDwellChange(parseInt(e.target.value, 10))}
              >
                <option value="5">{t('monitoring:dash.every.sec5')}</option>
                <option value="10">{t('monitoring:dash.every.sec10')}</option>
                <option value="20">{t('monitoring:dash.every.sec20')}</option>
                <option value="30">{t('monitoring:dash.every.sec30')}</option>
                <option value="60">{t('monitoring:dash.every.sec60')}</option>
              </select>
            </label>
          </div>
        </div>
      </div>

      {/* 분리된 탭들을 각각 DetachablePanel로 렌더 → 팝업 창으로 포탈 */}
      {Array.from(detachedTabIds).map(tabId => {
        const tab = tabs.find(t => t.id === tabId);
        if (!tab) return null;
        const popupIsFs = !!popupFullscreenMap[tabId];
        // popup 이 OS-fullscreen 일 때 wrapper 에 modalFullscreen className 추가하면
        // CSS 의 `.modalFullscreen .recharts-text { 13px !important }` 등이 적용돼
        // 차트 텍스트/제목/범례가 한꺼번에 스케일업. 패딩도 줄여 차트 공간 최대화.
        return (
          <DetachablePanel
            key={`detached-${tabId}`}
            title={t('monitoring:dash.windowTitle', { name: tab.name })}
            isDetached={true}
            onDetach={() => {}}
            onAttach={() => handleAttachTab(tabId)}
            defaultSize={{ width: 1100, height: 720 }}
          >
            <div
              className={`${popupIsFs ? styles.modalFullscreen : ''} ${styles.bodyFit}`}
              style={{
                padding: popupIsFs ? 8 : 16,
                width: '100%',
                height: '100%',
                minHeight: 0,
                minWidth: 0,
                boxSizing: 'border-box',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
              }}
            >
              <PopupFullscreenDetector
                onChange={(isFs) => setPopupFullscreenMap(prev =>
                  prev[tabId] === isFs ? prev : { ...prev, [tabId]: isFs }
                )}
              />
              {/* popup OS-fullscreen 상태를 isFullscreen 으로 전달 — 폰트/축 라벨 등 JS 단에서도 같이 스케일업 */}
              <TabContent tab={tab} {...tabContentProps} isFullscreen={popupIsFs} />
            </div>
          </DetachablePanel>
        );
      })}

      <PanelEditor
        open={editorState.open}
        panel={editorState.panel}
        slotIndex={editorState.slotIndex}
        datasources={datasources}
        queryTemplates={queryTemplates}
        nodeOptions={nodeList}
        tabPanels={tabs.find(t => t.id === editorState.tabId)?.panels || []}
        onSaveTemplate={handleSaveQueryTemplate}
        onClose={closeEditor}
        onSave={savePanel}
        onDelete={deletePanel}
      />
    </div>
  );
};

/* ====== 탭 내용 렌더러 ====== */

const TabContent = ({
  tab, API_BASE, minutes, range, timeDomain, presentation, isDarkMode, refreshTick, instances, demo, onReload, instanceId,
  tabs, settingsTabs, datasources, connections, rotationList, onToggleRotationInclude, onMoveRotation,
  onChangeLayout, onCreateTab, onDeleteTab, onRenameTab, onToggleHidden, onSaveAll,
  onEditPanel, onAddPanelAtSlot, onAddPanelAppend, onTogglePanel, onReorderPanel,
  onPanelElementClick, tabVariables, isFullscreen, displayMode,
}) => {
  if (!tab) return null;
  if (tab.kind === 'settings') {
    return (
      <SettingsTab
        tabs={settingsTabs || tabs}
        apiBase={`${API_BASE}/sql-developer`}
        onReload={onReload}
        onChangeLayout={onChangeLayout}
        onCreateTab={onCreateTab}
        onDeleteTab={onDeleteTab}
        onRenameTab={onRenameTab}
        onToggleHidden={onToggleHidden}
        onSaveAll={onSaveAll}
        rotationList={rotationList}
        onToggleRotationInclude={onToggleRotationInclude}
        onMoveRotation={onMoveRotation}
      />
    );
  }
  return (
    <PanelTabContentMemo
      tab={tab}
      API_BASE={API_BASE}
      demo={demo}
      instanceId={instanceId}
      presentation={presentation}
      datasources={datasources}
      minutes={minutes}
      range={range}
      timeDomain={timeDomain}
      isDarkMode={isDarkMode}
      refreshTick={refreshTick}
      onEditPanel={tab.canEdit === false ? null : onEditPanel}
      onAddPanelAtSlot={tab.canEdit === false ? null : onAddPanelAtSlot}
      onAddPanelAppend={tab.canEdit === false ? null : onAddPanelAppend}
      onTogglePanel={tab.canEdit === false ? null : onTogglePanel}
      onReorderPanel={tab.canEdit === false ? null : onReorderPanel}
      onPanelElementClick={onPanelElementClick}
      variables={tabVariables?.[tab.id] || NO_VARS}
      isFullscreen={isFullscreen}
      displayMode={displayMode}
    />
  );
};
const NO_VARS = Object.freeze({});

/**
 * 노드 목록 받기(2026-10-16, RAC) — 그리는 것이 없다. 대상이 바뀌면 다시 묻는다. 고르기는 패널 편집 화면의 「노드」 칸이다(2026-10-18)
 */
const NodeListLoader = ({ API_BASE, instanceId, demo, onList }) => {
  useEffect(() => {
    const done = (list) => { onList(list); };
    // 예시 데이터는 서버가 2노드로 준다 — 대상이 없어도 노드 고르기를 보인다
    if (!instanceId && !demo) { done([]); return undefined; }
    let alive = true;
    fetch(`${API_BASE}/sql-developer/monitoring/nodes?${demo ? 'demo=1' : `instanceId=${encodeURIComponent(instanceId)}`}`)
      .then(r => (r.ok ? r.json() : { nodes: [] }))
      .then(j => { if (alive) done(Array.isArray(j.nodes) ? j.nodes : []); })
      .catch(() => { if (alive) done([]); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [API_BASE, instanceId, demo]);
  return null;
};

/* ====== 빌트인/사용자 탭 (패널 그리드) ====== */

const PanelTabContent = ({
  tab, API_BASE, datasources, minutes, range, timeDomain, presentation, isDarkMode, refreshTick, demo, instanceId,
  onEditPanel, onAddPanelAtSlot, onAddPanelAppend, onTogglePanel, onReorderPanel,
  onPanelElementClick, variables, isFullscreen, displayMode,
}) => {
  const [panelData, setPanelData] = useState({}); // panel.id -> { data, error }

  // 읽을 곳이 비어 있는 (옛) 패널은 서버가 정한다 — 옛 기본 데이터소스가 있으면 그것, 없으면 수집 저장소(sourceRef)
  const defaultDsId = null;

  // tab 변수 (producer initial + 클릭 갱신값) — 의존성 비교용 stable key
  const variablesKey = useMemo(() => JSON.stringify(variables || {}), [variables]);

  // 패널이 SQL 에 실제로 보낼 binds 를 계산.
  // 1순위: panel.consumes (명시적 globalVar → bindName 매핑)
  // 폴백:  SQL 의 :name 이 tabVar 로 그대로 존재하면 자동 매핑 (구버전 호환)
  const computeBindsForPanel = (panel, vars) => {
    const out = {};
    for (const c of (panel.consumes || [])) {
      if (c.bindName && c.globalVar) {
        out[c.bindName] = vars[c.globalVar];
      }
    }
    const required = getRequiredVariables(panel.sql);
    for (const name of required) {
      if (out[name] === undefined && vars[name] !== undefined) {
        out[name] = vars[name];   // 폴백: 이름 그대로 매핑
      }
    }
    return out;
  };

  useEffect(() => {
    if (!tab?.panels) return;
    let canceled = false;
    const vars = variables || {};
    (async () => {
      const entries = await Promise.all(tab.panels.map(async (panel) => {
        // 비활성 패널은 fetch 안 함 (legacy 패널은 enabled 필드 없음 → 활성으로 간주)
        if (panel.enabled === false) {
          return [panel.id, { skipped: true, data: [] }];
        }
        // 병합 패널은 자체 SQL 이 없음 — sub-panel 들의 데이터를 lookup 만 함.
        // sub-panel 들은 같은 tab.panels 에 있으므로 이 루프에서 정상 fetch 됨.
        if (panel.chartType === 'merged') {
          return [panel.id, { merged: true, data: [] }];
        }
        // SQL 의 사용자 변수 (minutes/hours 제외) — consumes/폴백 으로 채워졌는지 확인
        const required = getRequiredVariables(panel.sql);
        const binds = computeBindsForPanel(panel, vars);
        const missing = required.filter(b => {
          const val = binds[b];
          return val === undefined || val === null || val === '';
        });
        if (missing.length > 0) {
          return [panel.id, { waiting: missing, data: [] }];
        }
        // 패널별 DS 결정: panel.dataSourceId 우선, 없으면 기본 DS
        const dataSourceId = panel.dataSourceId || defaultDsId;
        try {
          const res = await fetch(`${API_BASE}/sql-developer/monitoring/panels/execute`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ panel, tabId: tab.id, dataSourceId, minutes, variables: binds, ...(range ? { fromAt: range.fromAt, toAt: range.toAt } : {}), ...(instanceId ? { instanceId } : {}), ...(demo ? { demo: true } : {}) })
          });
          if (!res.ok) return [panel.id, { error: (await res.json().catch(() => ({}))).error || `HTTP ${res.status}`, data: [] }];
          const json = await res.json();
          return [panel.id, { data: json.data || [] }];
        } catch (err) {
          return [panel.id, { error: err.message, data: [] }];
        }
      }));
      if (canceled) return;
      setPanelData(Object.fromEntries(entries));
    })();
    return () => { canceled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, API_BASE, defaultDsId, minutes, range, refreshTick, variablesKey, demo, instanceId]);

  // panelId → panel 정의 lookup 맵. MergedPanelView 가 sub-panel 정의를 찾는 데 사용.
  const panelById = useMemo(() => {
    const m = new Map();
    (tab.panels || []).forEach(p => m.set(p.id, p));
    return m;
  }, [tab.panels]);

  return (
    <PanelSlotGrid
      tabId={tab.id}
      layout={tab.layout}
      panels={tab.panels}
      panelData={panelData}
      panelById={panelById}
      timeWindowMin={minutes}
      timeDomain={timeDomain}
      displayMode={displayMode}
      onEditPanel={tab.canEdit === false ? null : onEditPanel}
      onAddPanelAtSlot={tab.canEdit === false ? null : onAddPanelAtSlot}
      onAddPanelAppend={tab.canEdit === false ? null : onAddPanelAppend}
      onTogglePanel={tab.canEdit === false ? null : onTogglePanel}
      onReorderPanel={tab.canEdit === false ? null : onReorderPanel}
      onPanelElementClick={onPanelElementClick}
      variables={variables}
      isFullscreen={isFullscreen}
      isDarkMode={isDarkMode}
      presentation={presentation}
    />
  );
};

/**
 * 순환 중(presentation)에는 값이 같으면 다시 그리지 않는다(2026-10-18). 화면이 바뀔 때마다 모든 층(지금·나가는·다음 화면)의 차트를
 * 다시 그리느라 전환 첫 프레임이 200ms 쯤 멈췄다(프레임 측정). 순환 중에는 편집이 꺼져 있어(핸들러가 null 이거나 쓰이지 않는다)
 * 함수 칸이 새로 만들어진 것만으로 다시 그릴 까닭이 없다 — 함수는 견주지 않는다. 순환이 아닐 때는 늘 다시 그린다(예전 그대로)
 */
const samePanelTabProps = (a, b) => {
  if (!(a.presentation && b.presentation)) return false;
  const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
  for (const k of keys) {
    if (typeof a[k] === 'function' && typeof b[k] === 'function') continue;
    if (a[k] !== b[k]) return false;
  }
  return true;
};
const PanelTabContentMemo = React.memo(PanelTabContent, samePanelTabProps);

export default MonitoringDashboardModal;
